import { ChangeDetectorRef, Component, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { filter, Subscription } from 'rxjs';
import {
  Chat,
  ChatbotConfig,
  ChatbotRequest,
  ChatRecord,
  customerProfile,
  userData,
} from '../../shared/interfaces/chatbot.model';
import { CHATBOT_SHOW_STATUS } from '../../shared/constants/common.constants';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { ChatbotService } from '../../shared/services/chatbot.service';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { TraceIdService } from '../../shared/services/trace-id-service';
import { BusinessPlanResponse, ExamInfo, Package, PackageInfo, UserPlanResponse } from '../../shared/interfaces/reserve-loading.model';
import { UserPlanRequest } from '../../shared/interfaces/reserve-health.model';
import Decimal from 'decimal.js';
import { CartStateService } from '../../shared/services/cart-state.service';
import { CdkDrag, DragDropModule } from '@angular/cdk/drag-drop';
import { Location } from '@angular/common';
import { THREE_BAO_ITEMS } from '../../shared/constants/threee-bao.constants';
import { trigger } from '@angular/animations';
/**
 * 聊天機器人組件
 */
@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [MatInput, FormsModule, MatButton, NgClass, DragDropModule],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
})
export class ChatbotComponent implements OnInit, OnDestroy {
  translateService: TranslateService = inject(TranslateService);
  routerSubscription: Subscription = new Subscription();
  translateSubscription: Subscription = new Subscription();
  traceIdService: TraceIdService = inject(TraceIdService);
  private location = inject(Location); // 注入 Location 服務
  private cdr = inject(ChangeDetectorRef); // 注入 ChangeDetectorRef，用於手動觸發變更偵測
  private ngZone = inject(NgZone); // 注入 NgZone，確保 onUrlChange 回呼在 Angular Zone 內執行
  isShowChatbot: number = 1; // 是否顯示聊天機器人

  showStatus: number = CHATBOT_SHOW_STATUS.EXPANDED; // 當前聊天機器人顯示狀態
  text: string = ''; // 當前聊天機器人顯示文字
  isExpanded: boolean = false; // 對話框是否展開
  chat: Array<Chat> = [];
  isLoading: boolean = false;
  // 三寶項目
  threeBaoItems: Array<ExamInfo> = THREE_BAO_ITEMS;

  isCartOpen = false;
  private cartStateSubscription?: Subscription;
  @ViewChild(CdkDrag) dragInstance!: CdkDrag;
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private chatbotService: ChatbotService,
    private sharedStorageService: SharedStorageService,
    private cartStateService: CartStateService,
  ) { }

  // 據點轉換
  private centerMapping: { [key: string]: string } = {
    '01': '敦南',
    '02': '內湖',
    '04': '新竹',
    '05': '台南',
  };

  // 方案類型轉換
  private pageMapping: { [key: string]: string } = {
    '1': 'allCheckup',
    '2': 'aiEexclusive',
    '3': 'basicCheckup',
  };

  // 性別轉換
  private genderMapping: { [key: string]: string } = {
    'M': 'male',
    'F': 'female',
  };

  // 聊天記錄
  chatRecords: Array<ChatRecord> = [
    // {
    //   isBot: true,
    //   text: '如果您對檢查項目或預約流程有任何疑問，我可以隨時替您解惑！',
    // },
    // { isBot: false, text: '請問有過敏原檢測嗎？' },
    // {
    //   isBot: true,
    //   text: '目前我們提供過敏原檢測服務。如果您有興趣加做過敏原檢測，建議您直接聯繫我們的客服專員，以便了解詳細的檢測項目和費用。感謝您的詢問！',
    // },
  ];

  /**
   * 取得當前時間格式化字串
   */
  getCurrentTime(): string {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${ampm}`;
  }

  // 配置不同路由的按鈕顯示規則 - 初始化時不設定文字
  private chatbotConfigs: ChatbotConfig[] = [];

  // 在 ChatbotComponent 中加入 getter
  get currentRouteClass(): string {
    // 路由轉換過程中 snapshot 可能暫時不穩定，需做防護避免噴錯
    const activeRoute = this.getActiveRoute();
    const routeConfig = activeRoute?.snapshot?.routeConfig;
    const currentPath = routeConfig?.path ?? '';
    let classes = '';

    // 需要 bottom: 120px 的路由
    const routesWithHigherBottom = [
      'reserve-health',
      'reserve-add',
      'reserve-confirm-survey',
      'reserve-confirm-reminder',
      'appointment-info',
      'survey',
    ];

    if (routesWithHigherBottom.includes(currentPath)) {
      classes += 'higher-bottom';
    }
    if (currentPath === 'healthcheck') {
      classes += 'home-page';
    }
    if (currentPath === 'personalinfo') {
      classes += 'personal-info-page';
    }
    if (currentPath === 'personalplan') {
      classes += 'personal-plan-page';
    }
    if (currentPath === 'companyplan') {
      classes += 'company-plan-page';
    }

    // 當在加選頁且購物車開啟時
    if (currentPath === 'additem' && this.isCartOpen) {
      classes += 'cart-open ';
    }

    return classes.trim();
  }
  ngOnInit(): void {
    // 先更新 chatbot 狀態
    this.updateChatbotStatus();

    // 訂閱購物車狀態
    this.cartStateSubscription = this.cartStateService.isCartOpen$.subscribe(
      (isOpen: boolean) => {
        this.isCartOpen = isOpen;
      }
    );

    // 再初始化配置和記錄
    this.initializeChatbotConfigs();
    this.initializeChatRecords();

    // 監聽路由變化
    this.routerSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.getActiveRoute())
      )
      .subscribe((route: ActivatedRoute) => {
        // 每次路由變化時重新檢查 healthPlan 並更新配置
        this.updateChatbotStatus();
        this.initializeChatbotConfigs();
        this.updateButtonVisibility(route);
      });

    // 偵測子路由切換（personalhistory/familyhistory/symptoms 共用同一個 component，
    // 用 URL 路徑字串判斷，而非依賴元件實例重建），即時更新 greeting
    const greetingRoutes = ['personalhistory', 'familyhistory', 'symptoms'];
    this.location.onUrlChange((url: string) => {
      const lastSegment = url.split('/').pop() || '';
      if (!greetingRoutes.includes(lastSegment) || this.isExpanded) {
        return;
      }
      const config = this.chatbotConfigs.find(c => c.route === lastSegment);
      const greetingText = config?.greeting;
      if (!greetingText) {
        return;
      }
      // onUrlChange 是瀏覽器原生 API 回呼，不在 Angular Zone 內執行，
      // 用 NgZone.run() 包起來確保畫面能正確更新
      this.ngZone.run(() => {
        // 1. 移除所有舊 greeting
        const allGreetings = this.chatbotConfigs
          .filter(c => c.greeting)
          .map(c => c.greeting);

        const filteredRecords = this.chatRecords.filter(
          record => !(record.isBot && allGreetings.includes(record.text))
        );
        // 用解構賦值產生全新陣列引用，徹底解決 Angular 變更偵測盲區
        this.chatRecords = [
          ...filteredRecords,
          {
            isBot: true,
            text: greetingText,
            timestamp: this.getCurrentTime(),
          }
        ];
        // 放在 NgZone.run 內部執行，才能確保畫面同步更新
        this.cdr.detectChanges();
        //加上滾動置底，確保新訊息跑出來時使用者看得到
        this.scrollToBottom();
      });
    });

    // 監聽語言變化
    this.translateSubscription = this.translateService.onLangChange.subscribe(
      () => {
        this.updateChatbotStatus();
        this.initializeChatbotConfigs();
        this.updateButtonVisibility(this.getActiveRoute());
      }
    );

    // 初始化時檢查當前路由
    this.updateButtonVisibility(this.getActiveRoute());
  }
  /**
     * 展開對話框
     */

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.translateSubscription.unsubscribe();
    this.cartStateSubscription?.unsubscribe();
  }

  // 初始化聊天機器人配置
  private initializeChatbotConfigs(): void {

    // 若是特殊活動,不顯示chatbot
    if (this.sharedStorageService.get('healthPlan') === 'special') {
      this.chatbotConfigs = [];
      return;
    }

    // 確保翻譯已載入,否則延遲初始化
    if (!this.translateService.currentLang) {
      setTimeout(() => this.initializeChatbotConfigs(), 100);
      return;
    }

    // 使用 stream() 確保取得最新翻譯
    this.translateService.stream([
      'chatbot.reserve-home-text',
      'chatbot.reserve-health-text',
      'chatbot.reserve-result-text',
      'chatbot.business-reserve-result-text'
    ]).subscribe(() => {
      this.chatbotConfigs = [
        {
          route: 'healthcheck',
          text: this.translateService.instant('chatbot.reserve-home-text'),
          showStatus: CHATBOT_SHOW_STATUS.HIDE,
        },
        {
          route: 'location',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
          greeting: '您好，有任何健康檢查相關的問題都可以問我。',
        },
        {
          route: 'personalinfo',
          text: this.translateService.instant('chatbot.reserve-health-text'),
          showStatus: window.innerWidth < 768 ? CHATBOT_SHOW_STATUS.COLLAPSED : CHATBOT_SHOW_STATUS.EXPANDED,
          greeting: '您好，有任何資料填寫相關的問題都可以問我。'
        },
        {
          route: 'personalhistory',
          text: '',
          showStatus: this.isShowChatbot,
          greeting: '您好，有任何個人病史相關的問題都可以問我。'
        },
        {
          route: 'familyhistory',
          text: '',
          showStatus: this.isShowChatbot,
          greeting: '您好，有任何家族病史相關的問題都可以問我。'
        },
        {
          route: 'symptoms',
          text: '',
          showStatus: this.isShowChatbot,
          greeting: '您好，可以向我描述您不舒服的狀況，我可以建議勾選的項目。'
        },
        {
          route: 'loading',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.HIDE,
        },
        {
          route: 'personalplan',
          text: this.translateService.instant('chatbot.reserve-result-text'),
          showStatus: this.isShowChatbot,
          greeting: '您好，想了解方案的差異或內容，歡迎詢問我。'
        },
        {
          route: 'companyplan',
          text: this.translateService.instant(
            'chatbot.business-reserve-result-text'
          ),
          showStatus: CHATBOT_SHOW_STATUS.EXPANDED,
          greeting: '您好，可以告訴我您的需求或預算，我可以建議您適合的方案。'
        },
        {
          route: 'plancompare',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
          greeting: '您好，告訴我您最在意的部分，我來說明給您聽。'
        },
        {
          route: 'additem',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
          greeting: '您好，有任何看不懂或想了解的檢查項目，可以直接詢問我。'
        },
        {
          route: 'overview',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
          greeting: '您的預約內容都在這裡，確認無誤後即可預約。'
        },
        {
          route: 'evaluation',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
          greeting: '您好，有任何檢查前評估的問題都可以問我。'
        },
        {
          route: 'reminder',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
          greeting: '您好，確認過提醒內容會讓檢查更順利。'
        },
        {
          route: 'bookinfo',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
        },
        {
          route: 'timeselect',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
        },
        {
          route: 'review',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
        },
        {
          route: 'complete',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
        },
        {
          route: 'mybooking',
          text: '',
          showStatus: CHATBOT_SHOW_STATUS.COLLAPSED,
        },
      ];
    });
  }

  // 取得路由
  private getActiveRoute(): ActivatedRoute {
    let route = this.activatedRoute;
    while (route?.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  // 新增方法:更新 chatbot 顯示狀態
  private updateChatbotStatus(): void {
    const healthPlan = this.sharedStorageService.get('healthPlan');

    if (healthPlan === 'special') {
      this.isShowChatbot = CHATBOT_SHOW_STATUS.HIDE;
      this.showStatus = CHATBOT_SHOW_STATUS.HIDE;
    } else {
      if (this.router.url.includes('personalplan')) {
        if (window.innerWidth < 768) {
          this.isShowChatbot = CHATBOT_SHOW_STATUS.COLLAPSED;
        } else {
          this.isShowChatbot = CHATBOT_SHOW_STATUS.EXPANDED;
        }
      } else {
        this.isShowChatbot = CHATBOT_SHOW_STATUS.COLLAPSED;
      }
    }
  }

  // 更新聊天機器人按鈕的顯示狀態和文字
  private updateButtonVisibility(route: ActivatedRoute) {
    const routeConfig = route.snapshot.routeConfig;

    // 只有當 routeConfig 不存在時才隱藏，空字符串是有效路徑
    if (!routeConfig) {
      this.showStatus = CHATBOT_SHOW_STATUS.HIDE;
      return;
    }

    // 獲取路徑，如果是 undefined 則設為空字符串
    const currentPath = routeConfig.path ?? '';
    const config = this.getButtonConfig(currentPath);

    if (config) {
      this.showStatus = config.showStatus;
      this.text = config.text;
    } else {
      this.showStatus = CHATBOT_SHOW_STATUS.HIDE;
      this.text = '';
    }
    this.isExpanded = false;

    // 路由切換時：移除所有舊 greeting，只保留非 greeting 的對話紀錄
    const allGreetings = this.chatbotConfigs
      .filter(c => c.greeting)
      .map(c => c.greeting);
    this.chatRecords = this.chatRecords.filter(
      record => !(record.isBot && allGreetings.includes(record.text))
    );
  }

  // 取得對應路由的聊天機器人配置
  private getButtonConfig(routePath: string): ChatbotConfig | null {
    return (
      this.chatbotConfigs.find(config => config.route === routePath) || null
    );
  }

  // 點擊input
  onFocusInput() {
    this.scrollToBottom();
    this.isExpanded = true;
    this.text = ''; // 清空輸入框
  }

  onSendMessage(): void {
    const trimmedText = this.text?.trim();

    if (trimmedText && !this.isLoading) {
      // 取得當前路由
      const currentPath = this.getActiveRoute().snapshot.routeConfig?.path ?? '';

      // 如果是首頁的預設文字，不發送
      if (currentPath === 'healthcheck' && trimmedText === this.translateService.instant('chatbot.reserve-home-text')) {
        this.isExpanded = true;
        this.text = ''; // 清空輸入框
        return;
      }
      this.isLoading = true;
      this.isExpanded = true;
      // 加入用戶訊息
      this.chatRecords.push({
        isBot: false,
        text: trimmedText,
        timestamp: this.getCurrentTime(),
      });

      this.chat.push({
        role: 'user',
        content: trimmedText,
      });

      this.scrollToBottom();

      const orgNo = this.sharedStorageService.get('userPlanRequest')?.orgNo;
      const reserveType = this.sharedStorageService.get('reserveType') ?? '';
      let reservationType = '';

      if (reserveType === '1') {
        // 個人
        reservationType = 'individual';
      } else if (reserveType === '2') {
        // 企業
        reservationType = 'business';
      } else {
        // 會員中心
        reservationType = 'member';
      }

      console.log('this.router.url:', this.router.url.split('/').pop());

      const chatRequest: ChatbotRequest = {
        requestId: this.traceIdService.getCurrentTraceId(),
        reservationType: reservationType,
        center: orgNo ? this.centerMapping[orgNo] : '',
        messages: [{ role: 'user', content: trimmedText }],
        page: window.location.pathname.split('/').pop() ?? '',
        userData: this.getUserData(),
        stream: true,
      }

      // call api 傳送訊息
      if (trimmedText) {
        this.chatbotService.aiChatbot(chatRequest).subscribe(
          {
            next: (res) => {
              this.isLoading = false;
              const reply = res.data?.returnData?.response;
              // 測試用
              // const reply = '您好，很高興為您服務，需要安排健檢嗎？有問題點我發問吧！\n需要為您做什麼?\n沒事的話就回去瞜!';
              console.log("reply:", reply);
              if (reply) {
                this.addBotReply(reply);
              } else {
                this.addBotReply(this.translateService.instant('chatbot.error-text'));
              }
            },
            error: (error) => {
              this.isLoading = false;
              console.error('聊天機器人 API 錯誤:', error);
              this.addBotReply(this.translateService.instant('chatbot.system-error-text'));
            }
          }
        );
      }

      this.text = ''; // 清空輸入框

      // 如果需要模擬機器人回覆，可以加入：
      // setTimeout(() => {
      //   this.addBotReply("感謝您的訊息！");
      // }, 1000);
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const chatMsgBody = document.querySelector('.chat-msg-body');
      if (chatMsgBody) {
        chatMsgBody.scrollTop = chatMsgBody.scrollHeight;
      }
    }, 0);
  }

  private initializeChatRecords(): void {
    if (this.chatRecords.length === 0) {
      const currentPath = this.getActiveRoute().snapshot.routeConfig?.path ?? '';
      const config = this.getButtonConfig(currentPath);
      const greeting = config?.greeting || '很高興為您服務，需要安排健檢嗎？有問題點我發問吧！';

      // 避免重複加入相同 greeting
      const alreadyExists = this.chatRecords.some(
        record => record.isBot && record.text === greeting
      );
      if (!alreadyExists) {
        this.chatRecords.push({
          isBot: true,
          text: greeting,
          timestamp: this.getCurrentTime(),
        });
      }
    }
  }

  // 展開對話框
  expandChat(text?: string) {
    this.isExpanded = true;
    this.scrollToBottom(); // 無論哪種情況都先滾動到底部
    if (this.dragInstance) {
      this.dragInstance.reset();
    }

    // ReserveHealthComponent 是用 location.replaceState 切換頁面（personalhistory/familyhistory/symptoms），
    // 不會更新 Router 內部狀態，this.router.url 會是舊值，需直接讀瀏覽器網址
    const lastSegment = window.location.pathname.split('/').pop() || '';
    const currentConfig = this.chatbotConfigs.find(c => c.route === lastSegment);

    // 移除所有之前頁面的 greeting，只保留非 greeting 的對話紀錄
    const allGreetings = this.chatbotConfigs
      .filter(c => c.greeting)
      .map(c => c.greeting);
    this.chatRecords = this.chatRecords.filter(
      record => !(record.isBot && allGreetings.includes(record.text))
    );
    // 只 push 當前頁面的 greeting
    if (currentConfig?.greeting) {
      this.chatRecords.push({
        isBot: true,
        text: currentConfig.greeting,
        timestamp: this.getCurrentTime(),
      });
    }

    // 只送當前頁面的 text（且尚未送過）
    const currentText = currentConfig?.text || '';
    const alreadySent = this.chatRecords.some(
      record => !record.isBot && record.text === currentText
    );
    if (currentText && !alreadySent) {
      this.text = currentText;
      this.onSendMessage();
    } else {
      this.showStatus = CHATBOT_SHOW_STATUS.EXPANDED;
    }
  }

  // 關閉對話框
  closeChat() {
    this.isExpanded = false;
    this.showStatus = CHATBOT_SHOW_STATUS.COLLAPSED;
    if (this.dragInstance) {
      this.dragInstance.reset();
    }
  }

  private addBotReply(reply: string) {
    this.chatRecords.push({
      isBot: true,
      text: reply.replace(/\n/g, '<br>'),
      timestamp: this.getCurrentTime(), // 加入時間戳
    });
  }

  // 取得使用者資料
  private getUserData(): userData {
    const reserveType = this.sharedStorageService.get('reserveType') ?? '';
    const userPlanRequest = this.sharedStorageService.get('userPlanRequest') as UserPlanRequest;
    const userPlanResponse = this.sharedStorageService.get('userPlanResponse') as UserPlanResponse;
    const businessPlanResponse = this.sharedStorageService.get('businessPlanResponse') as BusinessPlanResponse;
    const examItemList = this.sharedStorageService.get('examItemList') as Array<ExamInfo>;
    const url = this.router.url;
    const partyDiscountRate = this.sharedStorageService.get('partyDiscountRate') ?? '1';
    // 根據選擇的方案類型決定 packages 結構
    let packages: any = {};

    // 預設回傳值
    const defaultUserData: userData = {
      customerProfile: this.getCustomerProfile(),
      individualPlan: {
        basicCheckup: { items: [], totalPrice: 0 },
        allCheckup: { items: [], totalPrice: 0 },
        aiEexclusive: { items: [], totalPrice: 0 },
        addonRecommendation: { items: [],total: 0 },
      },
      businessPackageList: [],
      selectedPlan: this.sharedStorageService.get('businessSelectedPackage')?.packageNo ?? '',
      pageHtml: this.getPageHtmlContext() || '',
    }

    console.log('url:', url);

    // 檢查必要資料是否存在
    if (!userPlanRequest?.age || !userPlanRequest?.gender) {
      return defaultUserData;
    }

    // 取得目前頁面的 HTML 內容
    let htmlContext = '';
    htmlContext = this.getPageHtmlContext();
    console.log('htmlContext:', htmlContext);

    // 個人預約
    if (reserveType === '1') {
      // 若是在選擇方案頁，三個方案都帶入
      if (userPlanResponse && url.includes('/healthcheck/reserve/personalplan')) {

        // 全方位方案
        if (userPlanResponse.allCheckup?.packageList) {
          const discountRate = userPlanResponse.allCheckup.discountRate || 1;
          const allCheckupItems = userPlanResponse.allCheckup.packageList
            .flatMap(packageInfo => packageInfo.examItemList.map(examInfo => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              let finalPrice = originalPrice;
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(discountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
                // 若有活動折扣率
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  // 四捨五入到個位整數
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                finalPrice = roundedPrice;
              }
              return {
                examName: examInfo.examName,
                examItem: examInfo.examItem,
                accumulatedPrice: 0,
                itemPrice: Number(examInfo.examPrice),
                reason: examInfo.examType || '',
                abnormalRatio: examInfo.abnormalRatio || 0
              };
            }))
          const discountedPrice = userPlanResponse.allCheckup.packageList.reduce((total, packageInfo) => {
            const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(discountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
                // 若有活動折扣率
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  // 四捨五入到個位整數
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                return sum + roundedPrice.toNumber();
              } else {
                return sum + originalPrice.toNumber();
              }
            }, 0);
            return total + packageTotal;
          }, 0);
          packages.allCheckup = {
            items: allCheckupItems,
            totalPrice: Number(discountedPrice),
          }
        }

        // 智能專屬方案
        if (userPlanResponse.aiExclusive?.packageList) {
          const discountRate = userPlanResponse.aiExclusive.discountRate || 1;
          const aiExclusiveItems = userPlanResponse.aiExclusive.packageList
            .flatMap(packageInfo => packageInfo.examItemList.map(examInfo => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              let finalPrice = originalPrice;
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(discountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
                // 若有活動折扣率
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  // 四捨五入到個位整數
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                finalPrice = roundedPrice;
              }
              return {
                examName: examInfo.examName,
                examItem: examInfo.examItem,
                accumulatedPrice: 0,
                itemPrice: Number(examInfo.examPrice),
                reason: examInfo.examType || '',
                abnormalRatio: examInfo.abnormalRatio || 0
              };
            }))

          const discountedPrice = userPlanResponse.aiExclusive.packageList.reduce((total, packageInfo) => {
            const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(discountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
                // 若有活動折扣率
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  // 四捨五入到個位整數
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                return sum + roundedPrice.toNumber();
              } else {
                return sum + originalPrice.toNumber();
              }
            }, 0);
            return total + packageTotal;
          }, 0);

          packages.aiEexclusive = {
            items: aiExclusiveItems,
            totalPrice: Number(discountedPrice),
          }
        }

        // 基礎健檢方案
        if (userPlanResponse.basicCheckup?.packageList) {
          const discountRate = userPlanResponse.basicCheckup.discountRate || 1;
          const basicCheckupItems = userPlanResponse.basicCheckup.packageList
            .flatMap(packageInfo => packageInfo.examItemList.map(examInfo => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              let finalPrice = originalPrice;
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(discountRate);  // 乘上折扣率
                let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
                // 若有活動折扣率
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  // 四捨五入到個位整數
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                finalPrice = roundedPrice;
              }
              return {
                examName: examInfo.examName,
                examItem: examInfo.examItem,
                accumulatedPrice: 0,
                itemPrice: Number(examInfo.examPrice),
                reason: examInfo.examType || '',
                abnormalRatio: examInfo.abnormalRatio || 0
              };
            }))

          const discountedPrice = userPlanResponse.basicCheckup.packageList.reduce((total, packageInfo) => {
            const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(discountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
                // 若有活動折扣率
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  // 四捨五入到個位整數
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                return sum + roundedPrice.toNumber();
              } else {
                return sum + originalPrice.toNumber();
              }
            }, 0);
            return total + packageTotal;
          }, 0);

          packages.basicCheckup = {
            items: basicCheckupItems,
            totalPrice: Number(discountedPrice),
          }
        }
        //AICHATBOT智能推薦清單
        if (userPlanResponse.addonRecommendation?.items) {
          const total = userPlanResponse.addonRecommendation.total || 0;
          const addonRecommendation = userPlanResponse.addonRecommendation.items.flatMap((packageInfo: any) => {
            return {
              examName: packageInfo.examName,
              examItem: packageInfo.examItem,
              score: Number(packageInfo.score),
              examPrice: Number(packageInfo.examPrice),
              reasons: packageInfo.reasons || '',
              triggeredBy: packageInfo.triggeredBy || 0,
            };
          })
          packages.addonRecommendation = {
            items: addonRecommendation,
            total: total,
          }

        }
        return {
          customerProfile: this.getCustomerProfile(),
          individualPlan: packages,
          businessPackageList: [],
          selectedPlan: this.transformPackPlanType(this.sharedStorageService.get('selectedPackagePlanType') ?? ''),
          pageHtml: htmlContext,
        };
      }

      const selectedPackagePlanType = this.sharedStorageService.get('selectedPackagePlanType') ?? '1';

      const totalPrice = examItemList?.reduce((total, examInfo) => total + (Number(examInfo.examPrice) || 0), 0) ?? 0;
      const originalPrice = new Decimal(totalPrice);

      const discountRate = this.movedDiscountRate(originalPrice);

      // 計算折扣後的價格
      const discountedPrice = examItemList?.reduce((total, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
        if (examInfo.rateStatus === true) {
          const discountedPrice = originalPrice.mul(discountRate);
          let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
          // 若有活動折扣率
          if (partyDiscountRate) {
            roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
            // 四捨五入到個位整數
            roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
          }
          return total + roundedPrice.toNumber();
        } else {
          return total + originalPrice.toNumber();
        }
      }, 0) ?? 0;

      // 初始化所有方案為空
      packages = {
        allCheckup: { items: [], totalPrice: 0 },
        aiEexclusive: { items: [], totalPrice: 0 },
        basicCheckup: { items: [], totalPrice: 0 },
        addonRecommendation: { items: [],total: 0 }
      };

      if (examItemList && examItemList.length > 0) {
        // 優先使用乘上折扣率的
        const aiChatbotExamItemList = this.sharedStorageService.get('aiChatbotExamItemList');
        const sourceList = aiChatbotExamItemList && aiChatbotExamItemList.length > 0 ? aiChatbotExamItemList : examItemList;

        const items = sourceList.map(examInfo => {
          let finalPrice = new Decimal(examInfo.examPrice || 0);

          // 檢查 rateStatus，為 true 才套用折扣
          const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
          if (examInfo.rateStatus === true) {
            let discountedPrice = finalPrice.mul(discountRate); // 乘上折扣率
            let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數

            // 若有活動折扣率
            if (partyDiscountRate) {
              roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
              roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
            }
            finalPrice = roundedPrice;
          }

          return {
            examName: examInfo.examName,
            examItem: examInfo.examItem,
            accumulatedPrice: '0',
            itemPrice: Number(examInfo.examPrice),
            reason: examInfo.examType || '',
            abnormalRatio: examInfo.abnormalRatio || 0
          };
        });

        // 根據方案類型設定對應的 key
        const packageKey = this.pageMapping[selectedPackagePlanType] || 'basicCheckup';

        // 如果在 reserve-add 頁面，優先使用 paymentAmount
        const currentPath = this.getActiveRoute().snapshot.routeConfig?.path ?? '';
        let finalTotalPrice = discountedPrice;

        const paymentAmount = this.sharedStorageService.get('paymentAmount');
        if (paymentAmount !== null && paymentAmount !== undefined) {
          finalTotalPrice = Number(paymentAmount);
        }

        packages[packageKey] = {
          items: items,
          totalPrice: finalTotalPrice
        };

        const userPlanResponse = this.sharedStorageService.get('userPlanResponse');

        // 如果當前不是全方位方案，填入全方位方案資料
        if (packageKey !== 'allCheckup' && userPlanResponse?.allCheckup?.packageList) {
          const allCheckupDiscountRate = userPlanResponse.allCheckup.discountRate || 1;
          const allCheckupItems = userPlanResponse.allCheckup.packageList
            .flatMap(packageInfo => packageInfo.examItemList.map(examInfo => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              let finalPrice = originalPrice;
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(allCheckupDiscountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10);
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                finalPrice = roundedPrice;
              }
              return {
                examName: examInfo.examName,
                examItem: examInfo.examItem,
                accumulatedPrice: 0,
                itemPrice: Number(examInfo.examPrice),
                reason: examInfo.examType || '',
                abnormalRatio: examInfo.abnormalRatio || 0
              };
            }));

          const allCheckupTotalPrice = userPlanResponse.allCheckup.packageList.reduce((total, packageInfo) => {
            const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(allCheckupDiscountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10);
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                return sum + roundedPrice.toNumber();
              } else {
                return sum + originalPrice.toNumber();
              }
            }, 0);
            return total + packageTotal;
          }, 0);

          packages.allCheckup = {
            items: allCheckupItems,
            totalPrice: Number(allCheckupTotalPrice)
          };
        }

        // 如果當前不是智能專屬方案，填入智能專屬方案資料
        if (packageKey !== 'aiEexclusive' && userPlanResponse?.aiExclusive?.packageList) {
          const aiExclusiveDiscountRate = userPlanResponse.aiExclusive.discountRate || 1;
          const aiExclusiveItems = userPlanResponse.aiExclusive.packageList
            .flatMap(packageInfo => packageInfo.examItemList.map(examInfo => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              let finalPrice = originalPrice;
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(aiExclusiveDiscountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10);
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                finalPrice = roundedPrice;
              }
              return {
                examName: examInfo.examName,
                examItem: examInfo.examItem,
                accumulatedPrice: 0,
                itemPrice: Number(examInfo.examPrice),
                reason: examInfo.examType || '',
                abnormalRatio: examInfo.abnormalRatio || 0
              };
            }));

          const aiExclusiveTotalPrice = userPlanResponse.aiExclusive.packageList.reduce((total, packageInfo) => {
            const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(aiExclusiveDiscountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10);
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                return sum + roundedPrice.toNumber();
              } else {
                return sum + originalPrice.toNumber();
              }
            }, 0);
            return total + packageTotal;
          }, 0);

          packages.aiEexclusive = {
            items: aiExclusiveItems,
            totalPrice: Number(aiExclusiveTotalPrice)
          };
        }

        // 如果當前不是基礎方案，填入基礎方案資料
        if (packageKey !== 'basicCheckup' && userPlanResponse?.basicCheckup?.packageList) {
          const basicCheckupDiscountRate = userPlanResponse.basicCheckup.discountRate || 1;
          const basicCheckupItems = userPlanResponse.basicCheckup.packageList
            .flatMap(packageInfo => packageInfo.examItemList.map(examInfo => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              let finalPrice = originalPrice;
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(basicCheckupDiscountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10);
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                finalPrice = roundedPrice;
              }
              return {
                examName: examInfo.examName,
                examItem: examInfo.examItem,
                accumulatedPrice: 0,
                itemPrice: Number(examInfo.examPrice),
                reason: examInfo.examType || '',
                abnormalRatio: examInfo.abnormalRatio || 0
              };
            }));

          const basicCheckupTotalPrice = userPlanResponse.basicCheckup.packageList.reduce((total, packageInfo) => {
            const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
              const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
              const isThreeBao = this.threeBaoItems.some(item => item.examItem === examInfo.examItem);
              if (examInfo.rateStatus === true) {
                let discountedPrice = originalPrice.mul(basicCheckupDiscountRate);
                let roundedPrice = discountedPrice.div(10).ceil().mul(10);
                if (partyDiscountRate) {
                  roundedPrice = roundedPrice.mul(new Decimal(partyDiscountRate));
                  roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
                }
                return sum + roundedPrice.toNumber();
              } else {
                return sum + originalPrice.toNumber();
              }
            }, 0);
            return total + packageTotal;
          }, 0);

          packages.basicCheckup = {
            items: basicCheckupItems,
            totalPrice: Number(basicCheckupTotalPrice)
          };
        }
        if (userPlanResponse?.addonRecommendation?.items) {
          const total = userPlanResponse.addonRecommendation.total || 0;
          const addonRecommendation = userPlanResponse.addonRecommendation.items.flatMap((packageInfo: any) => {
            return {
              examName: packageInfo.examName,
              examItem: packageInfo.examItem,
              score: Number(packageInfo.score),
              examPrice: Number(packageInfo.examPrice),
              reasons: packageInfo.reasons || '',
              triggeredBy: packageInfo.triggeredBy || 0,
            };
          })
          packages.addonRecommendation = {
            items: addonRecommendation,
            total: total,
          }

        }
        return {
          customerProfile: this.getCustomerProfile(),
          individualPlan: packages,
          businessPackageList: [],
          selectedPlan: this.transformPackPlanType(this.sharedStorageService.get('selectedPackagePlanType') ?? ''),
          pageHtml: htmlContext,
        };
      }
    }
    // 企業預約
    else {

      // 企業預約 individualPlan 固定帶空值
      const individualPlan = {
        basicCheckup: { items: [], totalPrice: 0 },
        allCheckup: { items: [], totalPrice: 0 },
        aiEexclusive: { items: [], totalPrice: 0 },
        addonRecommendation: { items: [], total: 0 },
      }

      // 若是在選擇方案頁面或是方案比較頁，則回傳所有方案
      // 並且當在方案比較頁時，把 selectedPlan 設為空值
      if (businessPlanResponse && (url.includes('/healthcheck/reserve/companyplan') || url.includes('/healthcheck/reserve/plancompare'))) {
        return {
          customerProfile: this.getCustomerProfile(),
          businessPackageList: businessPlanResponse?.packageList ?? [],
          individualPlan: individualPlan,
          selectedPlan: url.includes('/healthcheck/reserve/plancompare') ? '' : this.sharedStorageService.get('businessSelectedPackage')?.packageNo ?? '',
          pageHtml: htmlContext,
        };
      }

      const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');

      return {
        customerProfile: this.getCustomerProfile(),
        businessPackageList: businessSelectedPackage ? [businessSelectedPackage] : [],
        individualPlan: individualPlan,
        selectedPlan: businessSelectedPackage?.packageNo ?? '',
        pageHtml: htmlContext,
      }
    }
    return defaultUserData;
  }

  /**
   * 計算動態折扣率
   */
  movedDiscountRate(originalPrice: Decimal): Decimal {
    const packageNo = this.sharedStorageService.get('bookingInfo')?.packageNo ?? '';
    console.log("計算折扣率 - originalPrice:", originalPrice.toString());

    // 防護：如果 originalPrice 為負數或零，返回基礎折扣率
    if (originalPrice.lte(0)) {
      console.warn("originalPrice 異常:", originalPrice.toString());
      return new Decimal(1);
    }

    const userPlanResponse = this.sharedStorageService.get('userPlanResponse');

    //取得原始售價(未折扣)
    let allcheckupPrice = new Decimal(userPlanResponse?.allCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);
    let aiExclusivePrice = new Decimal(userPlanResponse?.aiExclusive?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);
    const basicCheckupPrice = new Decimal(userPlanResponse?.basicCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0);

    // 取得原始折扣率
    const allCheckupRate = new Decimal(userPlanResponse?.allCheckup?.discountRate ?? 0);
    const aiExclusiveRate = new Decimal(userPlanResponse?.aiExclusive?.discountRate ?? 0);
    const basicCheckupRate = new Decimal(userPlanResponse?.basicCheckup?.discountRate ?? 0);

    // 取得三寶金額
    const threeBaoPrice = this.threeBaoItems.reduce((total, item) => {
      return total.add(new Decimal(item.examPrice ?? 0));
    }, new Decimal(0));

    // 檢查是否選取三寶
    const hasThreeBaoSelected = this.sharedStorageService.get('examItemList')?.some(item =>
      this.threeBaoItems.some(tb => tb.examItem === item.examItem && item.selectFlag === 1)
    );
    // 避免重複扣除三寶
    if (hasThreeBaoSelected) {
      originalPrice = originalPrice.sub(threeBaoPrice);
    }

    // 判斷：若packageNo = 115000，且modifyType = 'modifyExam'，則不扣除三寶金額
    if (packageNo !== '115000') {
      // 先扣除三寶金額，避免三寶影響折扣率計算
      aiExclusivePrice = aiExclusivePrice.sub(threeBaoPrice);
      allcheckupPrice = allcheckupPrice.sub(threeBaoPrice);
    }

    // 當目前原價大於等於全方位方案價格時
    if (originalPrice.gte(allcheckupPrice)) {
      console.log("使用全方位折扣率");
      return allCheckupRate;
    }
    // 當目前原價大於等於智能專屬方案價格時
    else if (originalPrice.gte(aiExclusivePrice)) {
      if (originalPrice.eq(aiExclusivePrice)) {
        console.log("使用智能專屬折扣率");
        return aiExclusiveRate;
      }
      console.log("計算全方位與智能專屬之間的折扣率");
      return allCheckupRate.sub(
        (allCheckupRate.sub(aiExclusiveRate))
          .mul((allcheckupPrice.sub(originalPrice)))
          .div((allcheckupPrice.sub(aiExclusivePrice)))
      );
    }
    // 當目前原價大於等於基礎方案價格時
    else if (originalPrice.gte(basicCheckupPrice)) {
      if (originalPrice.eq(basicCheckupPrice)) {
        console.log("使用基礎折扣率");
        return basicCheckupRate;
      }
      console.log("計算智能專屬與基礎之間的折扣率");
      return aiExclusiveRate.sub(
        ((aiExclusiveRate.sub(basicCheckupRate))).mul((aiExclusivePrice.sub(originalPrice)))
          .div((aiExclusivePrice.sub(basicCheckupPrice)))
      );
    }
    // 當目前原價小於基礎方案價格時
    else {
      console.log("使用基礎折扣率");
      return new Decimal(basicCheckupRate);
    }
  }

  // 取得目前頁面的 HTML 內容
  private getPageHtmlContext(): string {
    const routerOutlet = document.querySelector('router-outlet');
    if (routerOutlet?.nextElementSibling) {
      return (routerOutlet.nextElementSibling as HTMLElement).innerHTML;
    }
    return '';
  }

  // 取得個人健調表資料
  private getCustomerProfile(): customerProfile {
    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    return {
      orgNo: userPlanRequest?.orgNo ?? '',
      idNo: userPlanRequest?.idNo ?? '',
      gender: this.genderMapping[userPlanRequest?.gender ?? ''] ?? '',
      age: userPlanRequest?.age ?? '',
      isPregnant: userPlanRequest?.femaleStatus ?? false,
      isMenopausal: userPlanRequest?.femaleMenopausal ?? false,
      history: userPlanRequest?.history ?? [],
      family: userPlanRequest?.family ?? [],
      subjectiveSymptoms: userPlanRequest?.subjectiveSymptoms ?? []
    }
  }

  // 將方案代號轉換為方案名稱
  private transformPackPlanType(selectedPackagePlanType: string): string {
    if (selectedPackagePlanType === '1') {
      return 'allCheckup';
    } else if (selectedPackagePlanType === '2') {
      return 'aiEexclusive';
    } else if (selectedPackagePlanType === '3') {
      return 'basicCheckup';
    }
    return '';

  }

  protected readonly CHATBOT_SHOW_STATUS = CHATBOT_SHOW_STATUS;
}
